import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import styled from "styled-components";
import type { IHelpsiteArticles } from "@types-contentful";
import { richTextHelper, colours, device } from "@helpers";
import { helpsiteTrackLink } from "@helpers/helpsite-mixpanel";
import { BLOCKS } from "@contentful/rich-text-types";
import Icon from "@components/Icon";

// Desktop: row with wrap, gap 30px
// Mobile: column, gap 20px
const TopicGrid = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: 20px;
	width: 100%;

	@media (min-width: ${device.laptop}) {
		flex-direction: row;
		flex-wrap: wrap;
		gap: 30px;
		justify-content: center;
		align-items: flex-start;
	}
`;

// Desktop: 354px width, 204px height, column layout, centered content
// Mobile: full width, horizontal layout (row), left-aligned text, justifyContent center
const TopicCard = styled(Link)`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: flex-start;
	gap: 11px;
	padding: 15px 10px;
	background-color: rgba(255, 255, 255, 0.5);
	border: 1px solid rgba(0, 0, 0, 0.15);
	border-radius: 10px;
	text-decoration: none;
	color: inherit;
	width: 100%;
	box-sizing: border-box;

	@media (min-width: ${device.laptop}) {
		flex-direction: column;
		align-items: center;
		width: 354px;
		height: 204px;
		background-color: ${colours.WHITE};
		padding: 20px;
		justify-content: flex-start;
	}
`;

const IconContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 50px;
	height: 50px;
	background-color: #f0f0f0;
	border-radius: 6px;
	flex-shrink: 0;
`;

const TopicContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	flex: 1;
	width: 100%;

	@media (min-width: ${device.laptop}) {
		align-items: center;
		text-align: center;
		flex: 0;
		width: auto;
	}
`;

const TopicTitle = styled.h3`
	font-family: var(--euclid-medium);
	font-size: 18px;
	font-weight: 500;
	line-height: 1.2679999669392903em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;
	text-align: left;

	@media (min-width: ${device.laptop}) {
		font-size: 20px;
		line-height: 1.268000030517578em;
		text-align: center;
		
	}
`;

const TopicContent = styled.div`
	font-family: var(--euclid-regular);
	font-size: 15px;
	font-weight: 400;
	line-height: 1.268000030517578em;
	color: ${colours.MIDNIGHT_BLUE};

	@media (min-width: ${device.laptop}) {
		font-size: 16px;
		line-height: 1.2680000066757202em;
		text-align: center;
	}

	p {
		margin: 0;
	}
`;

interface HelpTopicListProps {
	topics: IHelpsiteArticles[];
}

const HelpTopicList: React.FC<HelpTopicListProps> = ({ topics }) => {
	const router = useRouter();
	const pagePath = router.asPath.split("?")[0];

	const richTextOptions = {
		renderNode: {
			[BLOCKS.PARAGRAPH]: (node: any, children: any) => (
				<p style={{ marginBottom: "8px" }}>{children}</p>
			),
		},
	};

	return (
		<TopicGrid>
			{topics
				.filter(
					(topic) =>
						!!topic?.sys?.id &&
						!!topic?.fields?.topicsRelativeUrl &&
						!!topic?.fields?.title,
				)
				.map((topic) => {
				// topicsRelativeUrl is like "/about-felix-plans", construct as "/help/about-felix-plans"
				const topicUrl = `/help${topic.fields.topicsRelativeUrl}`;

				return (
					<TopicCard
						key={topic.sys.id}
						href={topicUrl}
						onClick={() =>
							helpsiteTrackLink({
								helpsite_section: "topic_list",
								page_path: pagePath,
								link_href: topicUrl,
								label: topic.fields.title,
							})
						}
					>
						{topic.fields.iconRef && (
							<IconContainer>
								<Icon
									iconName={topic.fields.iconRef}
									iconSizes={{
										iconMobile: 40,
										iconTablet: 40,
										iconLaptop: 40,
										iconDesktop: 40,
										iconDesktopLarge: 40,
									}}
									alt={topic.fields.title}
								/>
							</IconContainer>
						)}
						<TopicContentWrapper>
							<TopicTitle>{topic.fields.title}</TopicTitle>
							{topic.fields.content &&
								topic.fields.content.nodeType &&
								topic.fields.content.content &&
								Array.isArray(topic.fields.content.content) && (
									<TopicContent>
										{richTextHelper(topic.fields.content, richTextOptions)}
									</TopicContent>
								)}
						</TopicContentWrapper>
					</TopicCard>
				);
				})}
		</TopicGrid>
	);
};

export default HelpTopicList;
